import { TEMPLATE_CHANGE_SUMMARY_TOOLTIP_SUMMARY_FIELDS as SUMMARY_FIELDS } from "../../../constants/investigationAnalysisReview";

import React from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import SummarizeOutlinedIcon from "@mui/icons-material/SummarizeOutlined";

const getSummary = (row) => {
  const nestedObjects = Object.values(row || {}).filter(
    (value) => value && typeof value === "object" && !Array.isArray(value),
  );
  const candidates = [
    row?.template_change_summary,
    row?.change_summary,
    row?.template_changes,
    row?.copy_summary,
    row?.summary,
    ...nestedObjects,
    row,
  ];

  return candidates.find((candidate) =>
    SUMMARY_FIELDS.some(([key]) => candidate?.[key] !== undefined),
  );
};

const SummaryItem = ({ label, value }) => (
  <Box display="grid" gridTemplateColumns="1fr 28px 40px" gap={0.5}>
    <Box sx={{ bgcolor: "#DFEAFB", px: 1.5, py: 0.9 }}>
      <Typography fontSize={13} color="#101828" noWrap>
        {label}
      </Typography>
    </Box>
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ bgcolor: "#F2F4F7" }}
    >
      <Typography fontSize={13}>:</Typography>
    </Box>
    <Box
      display="flex"
      alignItems="center"
      justifyContent="center"
      sx={{ bgcolor: "#DFEAFB" }}
    >
      <Typography fontSize={13}>{value ?? 0}</Typography>
    </Box>
  </Box>
);

const TemplateChangeSummaryTooltip = ({ row }) => {
  const summary = getSummary(row);
  if (!summary) {
    return (
      <Box
        aria-hidden="true"
        sx={{ width: 34, height: 34, flexShrink: 0 }}
      />
    );
  }

  const leftFields = SUMMARY_FIELDS.filter((_, index) => index % 2 === 0);
  const rightFields = SUMMARY_FIELDS.filter((_, index) => index % 2 === 1);
  const version = row?.template_version;

  const content = (
    <Box sx={{ width: 620, bgcolor: "#FFFFFF" }}>
      <Box sx={{ bgcolor: "#1976B9", color: "#FFFFFF", px: 2, py: 1 }}>
        <Typography fontSize={16} fontWeight={600}>
          Summary
        </Typography>
      </Box>
      <Box sx={{ p: 2 }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          gap={2}
          mb={1.5}
        >
          <Box display="flex" alignItems="center" gap={1} minWidth={0}>
            <Typography fontSize={14} fontWeight={700} noWrap>
              {row?.batch_id || "Template changes"}
            </Typography>
            {version !== null && version !== undefined && (
              <Box
                component="span"
                sx={{
                  bgcolor: "#E0F2FE",
                  color: "#175CD3",
                  borderRadius: "14px",
                  px: 1,
                  py: 0.25,
                  fontSize: 12,
                  whiteSpace: "nowrap",
                }}
              >
                Version {version}
              </Box>
            )}
          </Box>
          {row?.batch_date && (
            <Typography fontSize={13} color="#344054" whiteSpace="nowrap">
              {row.batch_date}
            </Typography>
          )}
        </Box>
        <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1.25}>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {leftFields.map(([key, label]) => (
              <SummaryItem key={key} label={label} value={summary[key]} />
            ))}
          </Box>
          <Box display="flex" flexDirection="column" gap={0.5}>
            {rightFields.map(([key, label]) => (
              <SummaryItem key={key} label={label} value={summary[key]} />
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Tooltip
      title={content}
      placement="left-start"
      arrow
      enterDelay={200}
      leaveDelay={150}
      componentsProps={{
        tooltip: {
          sx: {
            maxWidth: "none",
            p: 0,
            bgcolor: "#FFFFFF",
            color: "#101828",
            border: "1px solid #D0D5DD",
            boxShadow: "0 8px 24px rgba(16, 24, 40, 0.18)",
          },
        },
        arrow: { sx: { color: "#FFFFFF" } },
      }}
    >
      <IconButton size="small" aria-label="View template change summary">
        <SummarizeOutlinedIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );
};

export default TemplateChangeSummaryTooltip;